
import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { KPISection } from "./sections/KPISection";
import { ChartSection } from "./sections/ChartSection";
import { RevenueAnalytics } from "./RevenueAnalytics";
import { RecruiterPerformance } from "./RecruiterPerformance";
import { 
  AnalyticsTabs, 
  AnalyticsTabsList, 
  AnalyticsTabTrigger, 
  AnalyticsTabContent 
} from "./components/enhanced/AnalyticsTabs";
import { LoadingSkeleton } from "./components/enhanced/LoadingSkeleton";
import { BarChart3, TrendingUp, Users, DollarSign } from "lucide-react";
import { useDateRangeFilter } from "./hooks/useDateRangeFilter";
import { useAnalyticsSettings } from "./hooks/useAnalyticsSettings";
import type { Candidate } from "@/components/candidates/types";
import type { Job } from "@/components/jobs/types";
import type { AnalyticsConfig } from "./components/AnalyticsSettings";

interface AnalyticsDashboardProps {
  dateRange?: { from: Date; to: Date };
  onRefresh?: () => void;
  settings?: AnalyticsConfig;
}

export const AnalyticsDashboard = ({ dateRange, onRefresh, settings: propSettings }: AnalyticsDashboardProps) => {
  const queryClient = useQueryClient();
  const { filterDataByDateRange } = useDateRangeFilter();
  const { settings: hookSettings } = useAnalyticsSettings();
  
  // Use prop settings if provided, otherwise use hook settings
  const settings = propSettings || hookSettings;

  // Fetch candidates data
  const { data: candidates = [], isLoading: candidatesLoading, error: candidatesError } = useQuery({
    queryKey: ['candidates'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('candidates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Candidate[];
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch jobs data
  const { data: jobs = [], isLoading: jobsLoading, error: jobsError } = useQuery({
    queryKey: ['jobs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('jobs')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as Job[];
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Fetch clients data
  const { data: clients = [], isLoading: clientsLoading, error: clientsError } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('clients')
        .select('id, name')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
    retry: 2,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const isLoading = candidatesLoading || jobsLoading || clientsLoading;
  const hasError = candidatesError || jobsError || clientsError;

  // Filter data by date range
  const filteredCandidates = useMemo(() => {
    if (!dateRange) return candidates;
    return filterDataByDateRange(candidates);
  }, [candidates, dateRange, filterDataByDateRange]);

  const filteredJobs = useMemo(() => {
    if (!dateRange) return jobs;
    return filterDataByDateRange(jobs);
  }, [jobs, dateRange, filterDataByDateRange]);

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['candidates'] });
    queryClient.invalidateQueries({ queryKey: ['jobs'] });
    queryClient.invalidateQueries({ queryKey: ['clients'] });
    onRefresh?.();
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <LoadingSkeleton type="metric" className="h-32" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <LoadingSkeleton type="chart" />
          <LoadingSkeleton type="chart" />
          <LoadingSkeleton type="chart" />
        </div>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="space-y-6">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-destructive mb-2">Failed to Load Analytics Data</h3>
          <p className="text-muted-foreground mb-4">
            There was an error loading your analytics data. Please try refreshing or contact support if the issue persists.
          </p>
          <button 
            onClick={handleRefresh}
            className="bg-primary text-primary-foreground px-4 py-2 rounded-md hover:bg-primary/90"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* KPIs Section */}
      {settings.showKPIs && <KPISection candidates={filteredCandidates} />}

      {/* Analytics Tabs */}
      <AnalyticsTabs defaultValue="overview" className="w-full">
        <AnalyticsTabsList>
          <AnalyticsTabTrigger 
            value="overview" 
            icon={<BarChart3 className="h-4 w-4" />} 
            label="Overview" 
            count={filteredCandidates.length}
          />
          {settings.showRecruiterPerformance && (
            <AnalyticsTabTrigger 
              value="performance" 
              icon={<TrendingUp className="h-4 w-4" />} 
              label="Performance" 
            />
          )}
          {settings.showRevenueAnalytics && (
            <AnalyticsTabTrigger 
              value="revenue" 
              icon={<DollarSign className="h-4 w-4" />} 
              label="Revenue" 
            />
          )}
        </AnalyticsTabsList>

        <AnalyticsTabContent value="overview">
          <ChartSection 
            candidates={filteredCandidates} 
            settings={settings}
          />
        </AnalyticsTabContent>

        {settings.showRecruiterPerformance && (
          <AnalyticsTabContent value="performance">
            <RecruiterPerformance candidates={filteredCandidates} jobs={filteredJobs} />
          </AnalyticsTabContent>
        )}

        {settings.showRevenueAnalytics && (
          <AnalyticsTabContent value="revenue">
            <RevenueAnalytics clients={clients} />
          </AnalyticsTabContent>
        )}
      </AnalyticsTabs>
    </div>
  );
};
